import React, { useState, useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { X, RotateCcw, Crown, Download, FileDown } from "lucide-react";
import Logo from "../components/Logo";
import "./SquadBuilder.css";

const FORMATIONS = {
    "6v6": [
        { id: 1, pos: "KL", top: 88, left: 50, name: "Kaleci", number: "1" },
        { id: 2, pos: "DF", top: 70, left: 25, name: "Defans 1", number: "2" },
        { id: 3, pos: "DF", top: 70, left: 75, name: "Defans 2", number: "3" },
        { id: 4, pos: "OS", top: 40, left: 25, name: "Orta Saha 1", number: "8" },
        { id: 5, pos: "OS", top: 40, left: 75, name: "Orta Saha 2", number: "10" },
        { id: 6, pos: "FV", top: 15, left: 50, name: "Forvet", number: "9" },
    ],
    "7v7": [
        { id: 1, pos: "KL", top: 88, left: 50, name: "Kaleci", number: "1" },
        { id: 2, pos: "DF", top: 72, left: 30, name: "Defans 1", number: "2" },
        { id: 3, pos: "DF", top: 72, left: 70, name: "Defans 2", number: "4" },
        { id: 4, pos: "OS", top: 45, left: 20, name: "Sol Kanat", number: "7" },
        { id: 5, pos: "OS", top: 45, left: 50, name: "Orta Saha", number: "10" },
        { id: 6, pos: "OS", top: 45, left: 80, name: "Sağ Kanat", number: "11" },
        { id: 7, pos: "FV", top: 15, left: 50, name: "Forvet", number: "9" },
    ],
    "8v8": [
        { id: 1, pos: "KL", top: 88, left: 50, name: "Kaleci", number: "1" },
        { id: 2, pos: "DF", top: 72, left: 20, name: "Defans 1", number: "2" },
        { id: 3, pos: "DF", top: 72, left: 50, name: "Defans 2", number: "3" },
        { id: 4, pos: "DF", top: 72, left: 80, name: "Defans 3", number: "4" },
        { id: 5, pos: "OS", top: 42, left: 20, name: "Orta Saha 1", number: "6" },
        { id: 6, pos: "OS", top: 42, left: 50, name: "Orta Saha 2", number: "8" },
        { id: 7, pos: "OS", top: 42, left: 80, name: "Orta Saha 3", number: "10" },
        { id: 8, pos: "FV", top: 15, left: 50, name: "Forvet", number: "9" },
    ],
    "11v11": [
        { id: 1, pos: "KL", top: 90, left: 50, name: "Kaleci", number: "1" },
        { id: 2, pos: "DF", top: 72, left: 15, name: "Sol Bek", number: "3" },
        { id: 3, pos: "DF", top: 76, left: 38, name: "Stoper 1", number: "4" },
        { id: 4, pos: "DF", top: 76, left: 62, name: "Stoper 2", number: "5" },
        { id: 5, pos: "DF", top: 72, left: 85, name: "Sağ Bek", number: "2" },
        { id: 6, pos: "OS", top: 48, left: 15, name: "Sol Kanat", number: "7" },
        { id: 7, pos: "OS", top: 52, left: 38, name: "Merkez OS 1", number: "6" },
        { id: 8, pos: "OS", top: 52, left: 62, name: "Merkez OS 2", number: "8" },
        { id: 9, pos: "OS", top: 48, left: 85, name: "Sağ Kanat", number: "11" },
        { id: 10, pos: "FV", top: 22, left: 35, name: "Forvet 1", number: "9" },
        { id: 11, pos: "FV", top: 22, left: 65, name: "Forvet 2", number: "10" },
    ],
};

// Forma boyutu, sahanın genişliğine oranlı (cqw = sahanın %'si) olarak
// belirlenir; en kalabalık satırdaki oyuncu sayısına göre hesaplanmıştır,
// böylece 6v6'dan 11v11'e kadar hepsi aynı "sıkışıklık oranını" korur.
const TOKEN_CQW = {
    "6v6": 26,
    "7v7": 18,
    "8v8": 18,
    "11v11": 13.5,
};

// Kaleci, gerçek maçlardaki gibi takımın forma renginden farklı bir renk giyer.
// Seçilen forma rengiyle karışmasın diye, ona en yakın olmayan bir zıt renk seçilir.
const GK_KIT_COLORS = ["#facc15", "#f97316", "#22c55e", "#111114"];

const pickGoalkeeperKit = (teamPrimary) => {
    const teamDark = shade(teamPrimary, -0.4).toLowerCase();
    return GK_KIT_COLORS.find((c) => c.toLowerCase() !== teamPrimary.toLowerCase() && c.toLowerCase() !== teamDark)
        || GK_KIT_COLORS[0];
};

const KIT_PRESETS = [
    { name: "Çim Yeşili", primary: "#16a34a" },
    { name: "Ateş Kırmızısı", primary: "#dc2626" },
    { name: "Kobalt Mavi", primary: "#2563eb" },
    { name: "Gece Siyahı", primary: "#111114" },
    { name: "Kar Beyazı", primary: "#eef2f6" },
    { name: "Şimşek Sarısı", primary: "#facc15" },
    { name: "Turuncu", primary: "#f97316" },
    { name: "Kraliyet Moru", primary: "#7c3aed" },
];

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const shade = (hex, amount) => {
    let c = hex.replace("#", "");
    if (c.length === 3) c = c.split("").map((x) => x + x).join("");
    const num = parseInt(c, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    r = clamp(Math.round(r + r * amount), 0, 255);
    g = clamp(Math.round(g + g * amount), 0, 255);
    b = clamp(Math.round(b + b * amount), 0, 255);
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const readableText = (hex) => {
    let c = hex.replace("#", "");
    if (c.length === 3) c = c.split("").map((x) => x + x).join("");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.62 ? "#0f172a" : "#ffffff";
};

const slugify = (s) =>
    (s || "kadro")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^\wğüşıöçĞÜŞİÖÇ-]/gi, "") || "kadro";

const Jersey = ({ number, primary, isCaptain }) => {
    const dark = shade(primary, -0.4);
    const txt = readableText(primary);
    return (
        <div className="token-jersey">
            <svg viewBox="0 0 100 100" className="token-jersey-svg" aria-hidden="true">
                <path d="M28 16 L22 30 L32 36 L34 20 Z" fill={dark} />
                <path d="M72 16 L78 30 L68 36 L66 20 Z" fill={dark} />
                <path
                    d="M28 16 L40 8 Q50 16 60 8 L72 16 L78 30 L68 36 L68 90 Q68 95 63 95 L37 95 Q32 95 32 90 L32 36 L22 30 Z"
                    fill={primary}
                />
                <path d="M40 8 Q50 16 60 8 L56 5 Q50 11 44 5 Z" fill={dark} />
            </svg>
            <span className="token-jersey-number" style={{ color: txt }}>
                {number}
            </span>
            {isCaptain && <span className="token-cap">C</span>}
        </div>
    );
};

const CircleToken = ({ player, primary }) => (
    <div
        className="token-circle"
        style={{
            background: primary,
            color: readableText(primary),
            borderColor: shade(primary, -0.25),
        }}
    >
        {player.photo ? (
            <img src={player.photo} alt="" />
        ) : (
            <span>{player.number}</span>
        )}
        {player.isCaptain && <span className="token-cap">C</span>}
    </div>
);

const PitchLines = () => (
    <svg
        className="sb-pitch-lines"
        viewBox="0 0 100 150"
        preserveAspectRatio="none"
        aria-hidden="true"
    >
        <rect x="3" y="3" width="94" height="144" />
        <line x1="3" y1="75" x2="97" y2="75" />
        <circle cx="50" cy="75" r="12" />
        <circle className="sb-fill" cx="50" cy="75" r="0.9" />

        {/* Üst kale */}
        <rect x="21.5" y="3" width="57" height="20" />
        <rect x="37" y="3" width="26" height="7.5" />
        <circle className="sb-fill" cx="50" cy="15" r="0.9" />
        <path d="M41 23 A 12 12 0 0 0 59 23" />

        {/* Alt kale */}
        <rect x="21.5" y="127" width="57" height="20" />
        <rect x="37" y="139.5" width="26" height="7.5" />
        <circle className="sb-fill" cx="50" cy="135" r="0.9" />
        <path d="M41 127 A 12 12 0 0 1 59 127" />

        {/* Korner yayları */}
        <path d="M3 6 A 3 3 0 0 0 6 3" />
        <path d="M94 3 A 3 3 0 0 0 97 6" />
        <path d="M6 147 A 3 3 0 0 0 3 144" />
        <path d="M97 144 A 3 3 0 0 0 94 147" />

        {/* Kaleler */}
        <rect className="sb-goal" x="43" y="1.2" width="14" height="1.8" />
        <rect className="sb-goal" x="43" y="147" width="14" height="1.8" />
    </svg>
);

const SquadBuilder = () => {
    const [squadName, setSquadName] = useState("SahaLive Kadrosu");
    const [formationType, setFormationType] = useState("7v7");
    const [iconType, setIconType] = useState("uniform");
    const [kit, setKit] = useState(KIT_PRESETS[0]);
    const [players, setPlayers] = useState(FORMATIONS["7v7"].map((p) => ({ ...p })));
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [exporting, setExporting] = useState(null); // null | 'png' | 'pdf'
    const [draggingId, setDraggingId] = useState(null);

    const pitchRef = useRef(null);
    const drag = useRef(null);

    useEffect(() => {
        setPlayers(FORMATIONS[formationType].map((p) => ({ ...p })));
    }, [formationType]);

    useEffect(() => {
        document.documentElement.classList.add("squad-builder-active");
        document.body.classList.add("squad-builder-active");
        return () => {
            document.documentElement.classList.remove("squad-builder-active");
            document.body.classList.remove("squad-builder-active");
        };
    }, []);

    // --- SÜRÜKLE / BIRAK (Pointer Events - masaüstü + mobil) ---
    const onPointerDown = (e, player) => {
        e.preventDefault();
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch (err) {
            /* bazı tarayıcılarda capture desteklenmez */
        }
        drag.current = {
            id: player.id,
            pid: e.pointerId,
            sx: e.clientX,
            sy: e.clientY,
            moved: false,
        };
        setDraggingId(player.id);
    };

    const onPointerMove = (e) => {
        const d = drag.current;
        if (!d || d.pid !== e.pointerId || !pitchRef.current) return;
        if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 5) return;
        d.moved = true;
        const rect = pitchRef.current.getBoundingClientRect();
        const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 4, 96);
        const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 5, 95);
        setPlayers((prev) =>
            prev.map((p) => (p.id === d.id ? { ...p, left: x, top: y } : p))
        );
    };

    const onPointerUp = (e) => {
        const d = drag.current;
        if (!d || d.pid !== e.pointerId) return;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch (err) {
            /* yoksay */
        }
        if (!d.moved) {
            const p = players.find((pl) => pl.id === d.id);
            if (p) setEditingPlayer(p);
        }
        drag.current = null;
        setDraggingId(null);
    };

    // --- DIŞA AKTARMA ---
    const captureBoard = async () => {
        return html2canvas(pitchRef.current, {
            backgroundColor: "#0d5a30",
            scale: (window.devicePixelRatio || 1) > 1 ? 2.5 : 2,
            useCORS: true,
            logging: false,
        });
    };

    const triggerDownload = (href, filename) => {
        const a = document.createElement("a");
        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handleExportPNG = async () => {
        if (exporting) return;
        setExporting("png");
        await new Promise((r) => setTimeout(r, 350));
        try {
            const canvas = await captureBoard();
            triggerDownload(canvas.toDataURL("image/png"), `${slugify(squadName)}.png`);
        } catch (err) {
            console.error("PNG dışa aktarma hatası:", err);
            alert("Görsel oluşturulamadı, lütfen tekrar deneyin.");
        } finally {
            setExporting(null);
        }
    };

    const handleExportPDF = async () => {
        if (exporting) return;
        setExporting("pdf");
        await new Promise((r) => setTimeout(r, 350));
        try {
            const canvas = await captureBoard();
            const img = canvas.toDataURL("image/png");
            const w = canvas.width;
            const h = canvas.height;
            const pdf = new jsPDF({
                orientation: h >= w ? "portrait" : "landscape",
                unit: "px",
                format: [w, h],
            });
            pdf.addImage(img, "PNG", 0, 0, w, h);
            pdf.save(`${slugify(squadName)}.pdf`);
        } catch (err) {
            console.error("PDF dışa aktarma hatası:", err);
            alert("PDF oluşturulamadı, lütfen tekrar deneyin.");
        } finally {
            setExporting(null);
        }
    };

    const handleReset = () => {
        if (window.confirm("Kadroyu sıfırlamak istediğinize emin misiniz?")) {
            setPlayers(FORMATIONS[formationType].map((p) => ({ ...p })));
        }
    };

    const updatePlayer = (id, data) => {
        setPlayers((prev) =>
            prev.map((p) => {
                if (data.isCaptain && p.id !== id) return { ...p, isCaptain: false };
                if (p.id === id) return { ...p, ...data };
                return p;
            })
        );
        setEditingPlayer(null);
    };

    return (
        <div className="squad-builder-page">
            <aside className="sb-sidebar">
                <div className="sb-sidebar-logo">
                    <Logo width="150px" lightText={true} />
                </div>

                <div className="sb-sidebar-section">
                    <span className="sb-section-label">Paylaş / İndir</span>
                    <div className="sb-export-row">
                        <button
                            className="sb-main-btn"
                            onClick={handleExportPNG}
                            disabled={!!exporting}
                        >
                            <Download size={16} />
                            {exporting === "png" ? "Hazırlanıyor..." : "PNG"}
                        </button>
                        <button
                            className="sb-secondary-btn"
                            onClick={handleExportPDF}
                            disabled={!!exporting}
                        >
                            <FileDown size={16} />
                            {exporting === "pdf" ? "..." : "PDF"}
                        </button>
                    </div>

                    <span className="sb-section-label">Kadro Adı</span>
                    <input
                        className="sb-sidebar-input"
                        value={squadName}
                        onChange={(e) => setSquadName(e.target.value)}
                        maxLength={40}
                    />

                    <span className="sb-section-label">Oyuncu Sayısı</span>
                    <div className="sb-options-grid">
                        {["6v6", "7v7", "8v8", "11v11"].map((type) => (
                            <button
                                key={type}
                                className={`sb-option-btn ${formationType === type ? "active" : ""}`}
                                onClick={() => setFormationType(type)}
                            >
                                {type}
                            </button>
                        ))}
                    </div>

                    <span className="sb-section-label">İkon Stili</span>
                    <div className="sb-options-grid">
                        <button
                            className={`sb-option-btn ${iconType === "uniform" ? "active" : ""}`}
                            onClick={() => setIconType("uniform")}
                        >
                            Forma
                        </button>
                        <button
                            className={`sb-option-btn ${iconType === "circle" ? "active" : ""}`}
                            onClick={() => setIconType("circle")}
                        >
                            Daire
                        </button>
                    </div>

                    <span className="sb-section-label">Forma Rengi</span>
                    <div className="sb-kit-row">
                        {KIT_PRESETS.map((k) => (
                            <button
                                key={k.name}
                                className={`sb-swatch ${kit.primary === k.primary ? "active" : ""}`}
                                style={{ background: k.primary }}
                                title={k.name}
                                onClick={() => setKit(k)}
                            />
                        ))}
                        <label className="sb-swatch sb-swatch-custom" title="Özel renk">
                            <input
                                type="color"
                                value={kit.primary}
                                onChange={(e) =>
                                    setKit({ name: "Özel", primary: e.target.value })
                                }
                            />
                        </label>
                    </div>
                </div>

                <div className="sb-sidebar-footer">
                    <button className="sb-option-btn sb-reset-btn" onClick={handleReset}>
                        <RotateCcw size={14} /> Sıfırla
                    </button>
                    <p className="sb-hint">
                        Oyuncuyu <b>sürükle</b> ile taşı, <b>dokun</b> ile düzenle.
                    </p>
                </div>
            </aside>

            <main className="sb-content">
                <div className="sb-glow sb-glow-1" />
                <div className="sb-glow sb-glow-2" />

                <div className="sb-content-panel">
                    <div className="sb-panel-header">
                        <div>
                            <p className="sb-eyebrow">Taktik Tahtası</p>
                            <h2>{squadName}</h2>
                        </div>
                        <div className="sb-header-chips">
                            <span className="sb-chip">{formationType}</span>
                            <span className="sb-chip">{players.length} Oyuncu</span>
                        </div>
                    </div>

                    <div className="sb-stage">
                        <div
                            className="sb-pitch"
                            ref={pitchRef}
                            style={{ "--token-cqw": TOKEN_CQW[formationType] }}
                            onPointerMove={onPointerMove}
                        >
                            <div className="sb-pitch-stripes" />
                            <div className="sb-pitch-glow" />
                            <PitchLines />

                            <div className="sb-pitch-watermark">
                                <Logo width="100%" lightText={true} />
                            </div>

                            <div className="sb-pitch-label">
                                <span>{squadName}</span>
                                <span className="sb-pitch-label-sub">
                                    {formationType} • SahaLive
                                </span>
                            </div>

                            {players.map((player) => {
                                const playerKit = player.pos === "KL" ? pickGoalkeeperKit(kit.primary) : kit.primary;
                                return (
                                    <div
                                        key={player.id}
                                        className={`sb-token ${draggingId === player.id ? "dragging" : ""}`}
                                        style={{ top: `${player.top}%`, left: `${player.left}%` }}
                                        onPointerDown={(e) => onPointerDown(e, player)}
                                        onPointerMove={onPointerMove}
                                        onPointerUp={onPointerUp}
                                        onPointerCancel={onPointerUp}
                                    >
                                        {iconType === "circle" ? (
                                            <CircleToken player={player} primary={playerKit} />
                                        ) : (
                                            <Jersey
                                                number={player.number}
                                                primary={playerKit}
                                                isCaptain={player.isCaptain}
                                            />
                                        )}
                                        <span className="sb-token-name">{player.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </main>

            {editingPlayer && (
                <div
                    className="sb-modal-overlay"
                    onClick={() => setEditingPlayer(null)}
                >
                    <div className="sb-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sb-modal-head">
                            <h4>Oyuncuyu Düzenle</h4>
                            <X
                                size={20}
                                className="sb-modal-close"
                                onClick={() => setEditingPlayer(null)}
                            />
                        </div>

                        <label className="sb-section-label">Oyuncu Adı</label>
                        <input
                            type="text"
                            className="sb-sidebar-input"
                            value={editingPlayer.name}
                            maxLength={22}
                            onChange={(e) =>
                                setEditingPlayer({ ...editingPlayer, name: e.target.value })
                            }
                        />

                        <label className="sb-section-label">Forma Numarası</label>
                        <input
                            type="text"
                            className="sb-sidebar-input"
                            value={editingPlayer.number}
                            maxLength={2}
                            onChange={(e) =>
                                setEditingPlayer({
                                    ...editingPlayer,
                                    number: e.target.value.replace(/[^0-9]/g, ""),
                                })
                            }
                        />

                        <button
                            className={`sb-option-btn sb-cap-toggle ${editingPlayer.isCaptain ? "active" : ""}`}
                            onClick={() =>
                                setEditingPlayer({
                                    ...editingPlayer,
                                    isCaptain: !editingPlayer.isCaptain,
                                })
                            }
                        >
                            <Crown size={16} />{" "}
                            {editingPlayer.isCaptain ? "Kaptan" : "Kaptan Yap"}
                        </button>

                        <button
                            className="sb-main-btn sb-modal-save"
                            onClick={() => updatePlayer(editingPlayer.id, editingPlayer)}
                        >
                            Kaydet
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SquadBuilder;
